import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import type { GridCellParams } from '@mui/x-data-grid-pro';
import { useTranslation } from 'react-i18next';
import type { DiscountInput } from '@proposify/pricing-engine';
import type { EnhancedTableRowType, TableType } from 'components/editor/grid/reduxStore/table.types';
import { ColumnTypeIdentifier } from 'components/editor/grid/reduxStore/table.types';
import { formatPriceWithDollarSign } from '../helper';
import { TableNumericCell } from '../tableUtils/customCellHelpers';

// RISK: the engine expresses percentage discounts as fractions ('0.2' = 20%)
// while every UI surface speaks 0–100. These two mappers are the single point
// where that conversion happens for the subtotal cell and the DiscountPanel.
// parseFloat here is display-only — the underlying values stay decimal strings.
export function formatDiscountIndicator(discount: DiscountInput, currencySymbol: string, decimalPlaces?: number): string {
  if (discount.type === 'percentage') {
    const percent = Number((parseFloat(discount.value) * 100).toFixed(2));
    return `-${percent}%`;
  }
  return `-${formatPriceWithDollarSign(discount.value, decimalPlaces, currencySymbol)}`;
}

interface PanelDiscountInput {
  type: 'percentage' | 'fixed';
  value: string;
}

export function toPanelDiscountInput(discount: DiscountInput | null | undefined): PanelDiscountInput | null {
  if (!discount) return null;
  if (discount.type === 'percentage') {
    return { type: 'percentage', value: String(Number((parseFloat(discount.value) * 100).toFixed(2))) };
  }
  return { type: 'fixed', value: discount.value };
}

// Inverse of toPanelDiscountInput — the persistence boundary. UI percent
// (0–100) becomes the engine's fraction ('20' → '0.2'); scope/stackable get
// the line-item defaults the engine expects on a row discount.
export function fromPanelDiscountInput(panel: PanelDiscountInput | null): DiscountInput | null {
  if (!panel) return null;
  if (panel.type === 'percentage') {
    return { type: 'percentage', value: String(Number((parseFloat(panel.value) / 100).toFixed(6))), scope: 'line', stackable: false };
  }
  return { type: 'fixed', value: panel.value, scope: 'line', stackable: false };
}

/**
 * The discount write path's table transform: returns a new TableType with the
 * target row's `discount` set (or cleared with null). Rows and table are new
 * references; untouched rows are reused, so memoized consumers don't re-render.
 */
export function buildDiscountedTable(tableData: TableType, rowId: string, panelDiscount: PanelDiscountInput | null): TableType {
  const engineDiscount = fromPanelDiscountInput(panelDiscount);
  return {
    ...tableData,
    rows: tableData.rows.map((row) => (row.id === rowId ? { ...row, discount: engineDiscount } : row)),
  };
}

/**
 * Manual-subtotal write transform (no PRICE column): writes the subtotal cell
 * value and manualSubtotalOverride as a pair — the same invariant
 * handleColumnAdd/updateRows maintain when they clear both together.
 */
export function buildManualSubtotalTable(tableData: TableType, rowId: string, subtotalField: string, value: string): TableType {
  return {
    ...tableData,
    rows: tableData.rows.map((row) => (row.id === rowId ? { ...row, [subtotalField]: value, manualSubtotalOverride: value } : row)),
  };
}

interface DiscountSubtotalCellProps {
  params: GridCellParams;
  numberOfDecimalPlaces?: number;
  documentCurrencySymbol?: string;
}

export const DiscountSubtotalCell = ({ params, numberOfDecimalPlaces, documentCurrencySymbol }: DiscountSubtotalCellProps) => {
  const { t } = useTranslation();
  const row = params.row as EnhancedTableRowType;
  const discount = row.discount;
  const calculation = row._pricingCalculation;
  const frequency = calculation?.billingFrequency ?? row.billingFrequency ?? 'one-time';
  const suffix = frequency !== 'one-time' ? t(`pricing.billing_frequency.suffix.${frequency}`) : '';
  const isValidDiscount =
    typeof discount === 'object' && discount !== null && discount.value != null && parseFloat(String(discount.value)) > 0;

  if (!isValidDiscount || !calculation) {
    return (
      <TableNumericCell
        params={params}
        columnType={ColumnTypeIdentifier.SUBTOTAL}
        numberOfDecimalPlaces={numberOfDecimalPlaces}
        documentCurrencySymbol={documentCurrencySymbol}
        valueSuffix={suffix || undefined}
      />
    );
  }

  const original = formatPriceWithDollarSign(calculation.originalSubtotal, numberOfDecimalPlaces, documentCurrencySymbol);
  const finalPrice = formatPriceWithDollarSign(calculation.discountedSubtotal, numberOfDecimalPlaces, documentCurrencySymbol);

  return (
    <Box className="py-numeric-cell-value" data-testid="discount-subtotal-cell" sx={{ width: '100%', textAlign: 'right' }}>
      <Typography
        variant="inherit"
        component="div"
        color="inherit"
        data-testid="discount-subtotal-indicator"
        sx={{ fontSize: '0.86em', opacity: 0.69 }}
      >
        {formatDiscountIndicator(discount, documentCurrencySymbol ?? '$', numberOfDecimalPlaces)}{' '}
        <Box component="span" sx={{ textDecoration: 'line-through' }}>
          {original}
        </Box>
      </Typography>
      <Typography variant="inherit" color="inherit" data-testid="discount-subtotal-final">
        {finalPrice}
        {suffix}
      </Typography>
    </Box>
  );
};
